import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/form';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { RATE_LIMITED_MESSAGE } from '../constants';
import { useRequestSignInLink } from '../hooks/use-request-sign-in-link';
import {
  EMAIL_HINT,
  SIGN_IN_EMAIL_DEFAULTS,
  type SignInEmailValues,
  signInEmailSchema,
} from '../schemas/sign-in-email-schema';
import type { SignInLinkData } from '../types';

type EmailSignInFormProps = { onSent: (link: SignInLinkData) => void };

export function EmailSignInForm({ onSent }: EmailSignInFormProps) {
  const form = useForm<SignInEmailValues>({
    resolver: zodResolver(signInEmailSchema),
    defaultValues: SIGN_IN_EMAIL_DEFAULTS,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });
  const { handleSubmit, register, setError, clearErrors, formState } = form;
  const { requestSignInLink } = useRequestSignInLink();

  const onSubmit = async ({ email }: SignInEmailValues) => {
    const outcome = await requestSignInLink(email);
    if (outcome.link) {
      onSent(outcome.link);
      return;
    }
    const message = outcome.errorCode === 'RATE_LIMITED' ? RATE_LIMITED_MESSAGE : EMAIL_HINT;
    setError('email', { type: 'server', message });
  };

  return (
    <FormProvider {...form}>
      <form
        data-enter=""
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className="flex flex-col gap-2.5"
      >
        <FormField name="email">
          <FormItem>
            <FormLabel>Work email</FormLabel>
            <FormControl>
              <Input
                size="lg"
                type="email"
                autoComplete="email"
                placeholder="name@company.com"
                {...register('email', { onChange: () => clearErrors('email') })}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          isBlock
          isLoading={formState.isSubmitting}
        >
          Email me a sign-in link
        </Button>
      </form>
    </FormProvider>
  );
}
